import { getRowsPerPage } from '@/ui/layout/rows-per-page';
import Period, { LAST_7_DAYS } from 'Types/app/period';
import { makeAutoObservable } from 'mobx';

import { userStore } from 'App/mstore';
import { recordingsService } from 'App/services';
import { ExportsResponse, IRecord } from 'App/services/RecordingsService';

export default class RecordingsStore {
  recordings: IRecord[] = [];
  loading: boolean;
  page = 1;
  total: number = 0;
  order: 'desc' | 'asc' = 'desc';
  search = '';
  startTimestamp = 0;
  endTimestamp = 0;
  rangeName: string = 'LAST_24_HOURS';
  period: any = Period({ rangeName: LAST_7_DAYS });

  exportedVideosList: ExportsResponse['data']['videos'] = [];
  currentUser = false;
  /** the call playing on the replay screen, in place of the list */
  openRecord: IRecord | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setCurrUser = (val: boolean) => {
    this.currentUser = val;
    this.page = 1;
  };

  setRecordings(records: IRecord[], total?: number) {
    this.total = total || 0;
    this.recordings = records;
  }

  updateSearch(val: string) {
    this.search = val;
    this.page = 1;
  }

  updateTimestamps(period: any): void {
    const { start, end, rangeName } = period;
    this.period = Period({ start, end, rangeName });
    this.page = 1;
  }

  updatePage(page: number) {
    this.page = page;
  }

  setOrder(order: 'desc' | 'asc') {
    this.order = order;
    this.page = 1;
  }

  openRecording = (r: IRecord | null) => {
    this.openRecord = r;
  };

  async fetchRecordings() {
    const filter = {
      page: this.page,
      limit: getRowsPerPage('recordings'),
      order: this.order,
      query: this.search,
      startTimestamp: this.period.start,
      endTimestamp: this.period.end,
      // "My videos": the API narrows to one author by user id
      userId: this.currentUser ? userStore.account.id : undefined,
    };

    this.loading = true;
    try {
      const response: { records: []; total: number } =
        await recordingsService.fetchRecordings(filter);
      this.setRecordings(response.records, response.total);
      return response.records;
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  async fetchRecordingUrl(id: number): Promise<string | undefined> {
    this.loading = true;
    try {
      const recording = await recordingsService.fetchRecording(id);
      return recording.URL;
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  /** Rejects on failure; callers say so. */
  async deleteRecording(id: number) {
    this.loading = true;
    try {
      return await recordingsService.deleteRecording(id);
    } finally {
      this.loading = false;
    }
  }

  /** Rejects on failure; callers say so. */
  async updateRecordingName(id: number, name: string) {
    this.loading = true;
    try {
      return await recordingsService.updateRecordingName(id, name);
    } finally {
      this.loading = false;
    }
  }

  triggerExport = async (sessionId: string) => {
    try {
      const resp = await recordingsService.triggerExport(sessionId);
      return resp.data.status;
    } catch (e) {
      console.error(e);
    }
  };

  private recordingsSeq = 0;

  /** `fresh`: another project — drop the old rows while its list loads */
  getRecordings = async (fresh = false) => {
    const seq = ++this.recordingsSeq;
    if (fresh) this.setExportedVideosList([]);
    this.loading = true;
    const params = {
      page: this.page,
      limit: 10,
      // order: this.order,
      // currentUser: this.currentUser,
      // query: this.search,
      // startTimestamp: this.period.start,
      // endTimestamp: this.period.end,
    };

    try {
      const resp = await recordingsService.getExports(params);
      if (seq !== this.recordingsSeq) return;
      this.setExportedVideosList(resp.data.videos ?? []);
      this.setTotal(resp.data.total);
    } catch (e) {
      console.error(e);
    } finally {
      if (seq === this.recordingsSeq) this.setLoading(false);
    }
  };

  setExportedVideosList = (list: ExportsResponse['data']['videos']) => {
    this.exportedVideosList = list;
  };

  setTotal = (total: number) => {
    this.total = total;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  /** Rejects on failure; callers say so. */
  getRecordingLink = async (sessionId: string): Promise<string> => {
    const resp = await recordingsService.getDownloadLink(sessionId);
    return resp.data;
  };

  /** Rejects on failure; callers say so. */
  deleteSessionRecording = async (sessionId: string) => {
    await recordingsService.deleteVideo(sessionId);
  };

  resetValues = () => {
    this.recordings = [];
    this.loading = false;
    this.page = 1;
    this.total = 0;
    this.order = 'desc';
    this.search = '';
    this.startTimestamp = 0;
    this.endTimestamp = 0;
    this.rangeName = 'LAST_24_HOURS';
    this.period = Period({ rangeName: LAST_7_DAYS });
    this.exportedVideosList = [];
  };
}
